'use client'

import React, { useMemo, useRef, useEffect } from 'react'
import { ISeriesApi, IChartApi, LineStyle, LineWidth, LineSeries, UTCTimestamp } from 'lightweight-charts'
import { 
  FibonacciExtension, 
  Point, 
  FibonacciLevel,
  calculateFibonacciExtensionPrices,
  DEFAULT_FIBONACCI_EXTENSION_LEVELS
} from '../../types/drawing'

interface FibonacciExtensionProps {
  drawing: FibonacciExtension
  chart: IChartApi
  isActive: boolean
  onUpdate?: (drawing: FibonacciExtension) => void
  onComplete?: (drawing: FibonacciExtension) => void
}

/**
 * Fibonacci Extension Drawing Component
 * 
 * Based on ABC pattern for price target projection:
 * - A to B: Initial move
 * - B to C: Retracement move
 * - Extension levels: Project from C based on AB move
 * - Key extension ratios: 127.2%, 161.8%, 261.8%
 * - Used for profit target identification
 */
const FibonacciExtensionComponent: React.FC<FibonacciExtensionProps> = ({
  drawing,
  chart,
  isActive,
  onUpdate,
  onComplete
}) => {
  const seriesRefs = useRef<Map<string, ISeriesApi<'Line'>>>(new Map())
  const containerRef = useRef<HTMLDivElement>(null)

  // Calculate Fibonacci extension levels based on ABC pattern
  const fibLevels = useMemo(() => {
    if (drawing.points.length < 3) return []
    
    const [pointA, pointB] = drawing.basePoints
    const pointC = drawing.extensionPoint
    
    return calculateFibonacciExtensionPrices(
      pointA,
      pointB,
      pointC,
      drawing.levels.length > 0 ? drawing.levels : DEFAULT_FIBONACCI_EXTENSION_LEVELS
    )
  }, [drawing.basePoints, drawing.extensionPoint, drawing.levels])

  // Create or update line series for each Fibonacci extension level
  useEffect(() => {
    if (!chart || drawing.points.length < 3) return

    const extensionPoint = drawing.extensionPoint
    
    // Clear existing series
    seriesRefs.current.forEach(series => {
      try {
        if (chart && series) {
          chart.removeSeries(series)
        }
      } catch (error) {
        // Safely ignore cleanup errors when chart is destroyed
        console.warn('Failed to remove series during cleanup:', error)
      }
    })
    seriesRefs.current.clear()

    // Create new series for each visible level
    fibLevels.forEach((level, index) => {
      if (!level.isVisible) return

      // Create line series for this level
      const lineSeries = chart.addSeries(LineSeries, {
        color: level.color || drawing.style.lineColor,
        lineWidth: drawing.style.lineWidth as LineWidth,
        lineStyle: drawing.style.lineStyle === 'dashed' ? LineStyle.Dashed : 
                   drawing.style.lineStyle === 'dotted' ? LineStyle.Dotted : 
                   LineStyle.Solid,
        title: `Fib Ext ${level.label}`,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false
      })

      // Calculate line points - extension lines are horizontal from C point
      const lineData = [
        {
          time: extensionPoint.time as UTCTimestamp,
          value: level.price
        },
        {
          time: 999999999999 as UTCTimestamp, // Extend to future
          value: level.price
        }
      ]

      lineSeries.setData(lineData)
      seriesRefs.current.set(`level-${index}`, lineSeries)
    })

    // Add ABC pattern lines (A-B-C connecting lines)
    const patternSeries = chart.addSeries(LineSeries, {
      color: drawing.style.lineColor,
      lineWidth: (drawing.style.lineWidth + 1) as LineWidth,
      lineStyle: LineStyle.Solid,
      title: 'ABC Pattern',
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false
    })

    const [pointA, pointB] = drawing.basePoints
    const pointC = drawing.extensionPoint

    const patternData = [
      { time: pointA.time as UTCTimestamp, value: pointA.price },
      { time: pointB.time as UTCTimestamp, value: pointB.price },
      { time: pointC.time as UTCTimestamp, value: pointC.price }
    ]

    patternSeries.setData(patternData)
    seriesRefs.current.set('pattern', patternSeries)

    return () => {
      seriesRefs.current.forEach(series => {
        try {
          if (chart && series) {
            chart.removeSeries(series)
          }
        } catch (error) {
          // Safely ignore cleanup errors when chart is destroyed
          console.warn('Failed to remove series during cleanup:', error)
        }
      })
      seriesRefs.current.clear()
    }
  }, [chart, drawing, fibLevels])

  // Render price labels and level text
  const renderLabels = () => {
    if (!drawing.showLabels && !drawing.showPrices) return null

    return (
      <div 
        ref={containerRef}
        style={{
          position: 'absolute',
          pointerEvents: 'none',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          marginRight: '12px',
          marginTop: '110px',
          marginLeft: '10px',
        }}
      >
        {fibLevels.map((level, index) => {
          if (!level.isVisible) return null

          return (
            <div
              key={`ext-label-${index}`}
              style={{
                position: 'relative',
                color: level.color || drawing.style.textColor,
                fontSize: `${drawing.style.fontSize}px`,
                fontWeight: drawing.style.fontSize > 12 ? 'bold' : 'normal',
                background: drawing.showBackground ? 'rgba(0, 0, 0, 0.7)' : 'transparent',
                padding: drawing.showBackground ? '2px 6px' : '0',
                borderRadius: '4px',
                whiteSpace: 'nowrap'
              }}
            >
              {drawing.showLabels && `Ext ${level.label}`}
              {drawing.showLabels && drawing.showPrices && ' - '}
              {drawing.showPrices && `$${level.price.toFixed(4)}`}
            </div>
          )
        })}
        
        {/* ABC Pattern Labels */}
        <div
          style={{
            position: 'absolute',
            color: drawing.style.textColor,
            fontSize: `${Math.max(10, drawing.style.fontSize - 2)}px`,
            fontWeight: 'bold',
            background: 'rgba(0, 0, 0, 0.8)',
            padding: '2px 4px',
            borderRadius: '3px'
          }}
        >
          A
        </div>
        <div
          style={{
            position: 'absolute',
            color: drawing.style.textColor,
            fontSize: `${Math.max(10, drawing.style.fontSize - 2)}px`,
            fontWeight: 'bold',
            background: 'rgba(0, 0, 0, 0.8)',
            padding: '2px 4px',
            borderRadius: '3px'
          }}
        >
          B
        </div>
        <div
          style={{
            position: 'absolute',
            color: drawing.style.textColor,
            fontSize: `${Math.max(10, drawing.style.fontSize - 2)}px`,
            fontWeight: 'bold',
            background: 'rgba(0, 0, 0, 0.8)',
            padding: '2px 4px',
            borderRadius: '3px'
          }}
        >
          C
        </div>
      </div>
    )
  }

  // Handle mouse interactions for editing
  const handleMouseEvents = () => {
    if (!isActive || !chart) return

    // TODO: Implement mouse event handlers for editing
    // - Click and drag to move ABC points
    // - Right-click for context menu
    // - Handle point manipulation
  }

  return (
    <>
      {renderLabels()}
      {isActive && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'auto',
            cursor: 'crosshair'
          }}
          onMouseDown={handleMouseEvents}
        />
      )}
    </>
  )
}

export default FibonacciExtensionComponent

// Fibonacci Extension Factory
export const createFibonacciExtension = (
  id: string,
  pointA: Point,
  pointB: Point,
  pointC: Point,
  options?: Partial<FibonacciExtension>
): FibonacciExtension => {
  const levels = calculateFibonacciExtensionPrices(
    pointA,
    pointB,
    pointC,
    DEFAULT_FIBONACCI_EXTENSION_LEVELS
  )

  return {
    id,
    type: 'fibonacci-extension',
    points: [pointA, pointB, pointC],
    basePoints: [pointA, pointB],
    extensionPoint: pointC,
    levels,
    showPrices: true,
    showLabels: true,
    showBackground: false,
    style: {
      lineColor: '#FF6B35',
      lineWidth: 1,
      lineStyle: 'solid',
      textColor: '#E0E3EB',
      fontSize: 12
    },
    isSelected: false,
    isVisible: true,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    zIndex: 1,
    ...options
  }
}

// Utility functions for Fibonacci extensions
export const calculateABMove = (pointA: Point, pointB: Point): number => {
  return pointB.price - pointA.price
}

export const calculateBCRetracement = (pointA: Point, pointB: Point, pointC: Point): number => {
  const abMove = calculateABMove(pointA, pointB)
  const bcMove = pointC.price - pointB.price
  return Math.abs(bcMove / abMove)
}

export const isValidABCPattern = (
  pointA: Point,
  pointB: Point,
  pointC: Point,
  maxRetracement: number = 0.786
): boolean => {
  const retracement = calculateBCRetracement(pointA, pointB, pointC)
  return retracement <= maxRetracement && retracement >= 0.236
}

export const getExtensionTargets = (
  pointA: Point,
  pointB: Point,
  pointC: Point
): { target127: number; target162: number; target262: number } => {
  const abMove = calculateABMove(pointA, pointB)
  
  return {
    target127: pointC.price + (1.272 * abMove),
    target162: pointC.price + (1.618 * abMove),
    target262: pointC.price + (2.618 * abMove)
  }
}

// Popular Fibonacci extension configurations
export const FIBONACCI_EXTENSION_PRESETS = {
  conservative: [1, 1.272, 1.618],
  standard: [1, 1.272, 1.414, 1.618, 2.618],
  aggressive: [1, 1.272, 1.414, 1.618, 2, 2.618, 3.618],
  crypto: [1, 1.272, 1.618, 2.618, 4.236, 6.854]
} 